import React, { useEffect, useRef, useCallback } from "react";
import clsx from "clsx";
import styles from "./styles.module.css";
import ScoreBalloon from "./ScoreBalloon";
import { getWidth } from "../../../utils/relativeSizes";
import { useSelector, useDispatch } from "react-redux";
import { _isNil } from "../../../utils/helpers/zupeeLodash";
import { getScoreFlyAnimationConfig } from "../../../store/selectors/ludoboardSelectors";
import { resetScoreFlyAnimationConfigForToken } from "../../../store/actions/ludoboardActions";

interface ICoordinates {
  translateX: number;
  translateY: number;
}

interface IScoreFlyAnimationProps {
  initialPosition: ICoordinates;
  finalPosition: ICoordinates;
  score: number;
  onAnimationEnd?: () => void;
}

const ScoreFlyAnimation: React.FC<IScoreFlyAnimationProps> = ({
  initialPosition,
  finalPosition,
  score,
  onAnimationEnd,
}) => {
  const balloonRef = useRef<HTMLDivElement | null>(null);
  const animationTimeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    const balloon = balloonRef.current;

    if (balloon) {
      // Reset any existing transitions
      balloon.style.transition = "none";
      balloon.style.transform = `translate(${initialPosition.translateX}px, ${initialPosition.translateY}px) scale(1)`;
      balloon.style.opacity = "1";

      // Force a reflow
      void balloon.offsetHeight;

      // Start the movement animation
      balloon.style.transition = "transform 1.2s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s linear";
      balloon.style.transform = `translate(${finalPosition.translateX}px, ${finalPosition.translateY}px) scale(1.2)`;

      // Hold at final position
      animationTimeoutRef.current = setTimeout(() => {
        balloon.style.transition = "opacity 0.5s ease-out";
        balloon.style.opacity = "0";
        
        // Trigger onAnimationEnd after fade out
        setTimeout(() => {
          if (onAnimationEnd) {
            onAnimationEnd();
          }
        }, 500);
      }, 1200);

      return () => {
        if (animationTimeoutRef.current) {
          clearTimeout(animationTimeoutRef.current);
        }
      };
    }
  }, [finalPosition, initialPosition, onAnimationEnd]);

  return (
    <div className={clsx(styles.scoreFlyAnimationContainer)}>
      <div
        ref={balloonRef}
        className={clsx(styles.animatedBalloon)}
        style={{
          transform: `translate(${initialPosition.translateX}px, ${initialPosition.translateY}px) scale(1)`,
          opacity: 1,
          width: getWidth(40),
          height: getWidth(40),
          position: 'absolute',
        }}
      >
        <ScoreBalloon score={score} />
      </div>
    </div>
  );
};

const ScoreFlyAnimationWrapper: React.FC<{ playerNumber: number }> = ({
  playerNumber,
}) => {
  const dispatch = useDispatch();
  const scoreFlyAnimationConfig = useSelector(
    getScoreFlyAnimationConfig.bind(null, playerNumber)
  );

  const handleAnimationEnd = useCallback(() => {
    dispatch(resetScoreFlyAnimationConfigForToken(playerNumber));
  }, [dispatch, playerNumber]);

  useEffect(() => {
    // Cleanup function to reset animation config when component unmounts
    // or when animation config changes
    return () => {
      dispatch(resetScoreFlyAnimationConfigForToken(playerNumber));
    };
  }, [dispatch, playerNumber, scoreFlyAnimationConfig?.timestamp]);

  if (_isNil(scoreFlyAnimationConfig)) {
    return null;
  }

  return (
    <ScoreFlyAnimation
      key={`${playerNumber}-${scoreFlyAnimationConfig.score}-${scoreFlyAnimationConfig.timestamp}`}
      initialPosition={scoreFlyAnimationConfig.initialPosition}
      finalPosition={scoreFlyAnimationConfig.finalPosition}
      score={scoreFlyAnimationConfig.score}
      onAnimationEnd={handleAnimationEnd}
    />
  );
};

export default ScoreFlyAnimationWrapper;
